import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { fetchResiliation, resilierAbonnement } from '../services/api';
import { euros } from '../utils/format';

/** « 30 septembre 2026 » — une date qu'on lit sans la déchiffrer. */
const jour = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

/**
 * Résilier l'abonnement, depuis l'espace.
 *
 * Un client qui veut partir doit pouvoir partir sans écrire à personne ni
 * attendre de réponse. Un abonnement qu'on ne résilie qu'en négociant avec
 * quelqu'un est un abonnement dont on finit par faire opposition en banque,
 * ce qui coûte plus cher à tout le monde et laisse un mauvais souvenir.
 *
 * Le bloc dit d'abord ce qui se passe, ensuite seulement il propose le
 * bouton : jusqu'à quand le service tient, combien de temps les données
 * restent récupérables, et que rien n'est effacé dans l'immédiat.
 *
 * Le mot de passe est redemandé — même règle que la mise en production. Une
 * décision de cette portée ne s'appuie pas sur une session laissée ouverte
 * sur l'ordinateur du club.
 */
export default function ResilierAbonnement() {
  const [ouvert, setOuvert] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [motif, setMotif] = useState('');

  const query = useQuery({
    queryKey: ['admin-resiliation'],
    queryFn: fetchResiliation,
    retry: false,
  });

  const resilier = useMutation({
    mutationFn: () => resilierAbonnement(motDePasse, motif),
    onSuccess: () => {
      setMotDePasse('');
      query.refetch();
    },
  });

  if (query.isPending) return null;

  const etat = query.data ?? {};

  // Le laboratoire et la démonstration ne se résilient pas : la boutique ne
  // les connaît pas, et un bouton sans effet est pire qu'un bouton absent.
  if (etat.resiliable !== true) return null;

  if (resilier.isSuccess) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3 text-sm text-green-800 dark:text-green-200 flex items-start gap-2">
        <Check className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{(resilier.data as any)?.message}</span>
      </div>
    );
  }

  const fin = jour(etat.finDeService ?? null);

  return (
    <div className="rounded-xl border border-red-200 dark:border-red-900 p-4 sm:p-5">
      <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <AlertTriangle className="w-4 h-4 text-red-600" />
        Résilier l'abonnement
      </p>

      {/* Ce qui est réellement souscrit, et à quel prix. Résilier sans savoir
          ce qu'on résilie n'est pas une décision : le bureau qui hésite doit
          voir la somme en face, et le détail de ce qu'elle recouvre. */}
      {(etat.socle || (etat.options ?? []).length > 0) && (
        <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Ce que vous payez aujourd'hui
          </p>
          <ul className="mt-1.5 space-y-1">
            {etat.socle && (
              <li className="flex justify-between gap-3">
                <span className="text-gray-700 dark:text-gray-300">{etat.socle.nom}</span>
                <span className="tabular-nums shrink-0">{euros.format(etat.socle.prix)}</span>
              </li>
            )}
            {(etat.options ?? []).map((o: any) => (
              <li key={o.code} className="flex justify-between gap-3">
                <span className="text-gray-700 dark:text-gray-300">{o.nom}</span>
                <span className="tabular-nums shrink-0">{euros.format(o.prix)}</span>
              </li>
            ))}
          </ul>
          {etat.total !== null && etat.total !== undefined && (
            <p className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between gap-3 font-semibold text-gray-900 dark:text-gray-100">
              <span>Total {etat.periodicite === 'mensuel' ? 'par mois' : 'par an'}</span>
              <span className="tabular-nums">{euros.format(etat.total)}</span>
            </p>
          )}
        </div>
      )}

      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-1.5">
        <p>
          Les prélèvements s'arrêtent immédiatement.{' '}
          {fin ? (
            <>
              Votre espace reste accessible jusqu'au <strong>{fin}</strong>, terme de la période que
              vous avez réglée : ce qui est payé vous reste dû.
            </>
          ) : (
            <>Votre espace reste accessible jusqu'au terme de la période que vous avez réglée.</>
          )}
        </p>
        <p>
          Passé cette date, vous disposez encore de <strong>trente jours</strong> pour récupérer vos
          données. Au-delà, l'espace et ses sauvegardes sont détruits sans retour possible.
        </p>
        <p>Rien n'est effacé aujourd'hui, et vous pouvez revenir sur votre décision d'ici là.</p>
      </div>

      {!ouvert ? (
        <button
          onClick={() => setOuvert(true)}
          className="mt-4 px-4 py-2 rounded-lg border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          Résilier mon abonnement
        </button>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
              Ce qui vous a manqué, si vous voulez bien nous le dire
              <span className="text-gray-400"> (facultatif)</span>
            </label>
            <textarea
              rows={2}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              className="input-field"
              placeholder="Trop cher, fonction manquante, association dissoute…"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
              Votre mot de passe, pour confirmer
            </label>
            <input
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              className="input-field"
              autoComplete="current-password"
            />
          </div>

          {resilier.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {(resilier.error as any)?.message ?? 'La résiliation a échoué : réessayez.'}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => resilier.mutate()}
              disabled={resilier.isPending || motDePasse === ''}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60"
            >
              {resilier.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirmer la résiliation
            </button>
            <button
              onClick={() => {
                setOuvert(false);
                setMotDePasse('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
